import type { ChipTone } from '@/components/common/StatusChip';
import type { LeadStatus } from '@/types/shared';

/**
 * Mirrors `sales.service.ts`'s `TRANSITIONS` graph exactly (03-rbac-and-lifecycle.md §3) — the UI
 * only ever offers a next-status option the server would actually accept. `'Registered'` never
 * appears as a target here: it's reachable exclusively via `POST /leads/{id}/convert`, never via
 * `PATCH /leads/{id}/status`, even though it's `Attended`'s eventual real-world destination.
 */
export const LEAD_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  'New Lead': ['Contacted'],
  Contacted: ['Waiting', 'Interested', 'Lost'],
  Waiting: ['Interested', 'Contacted', 'Lost', 'Follow-up Later'],
  Interested: ['Appointment Set', 'Lost', 'Follow-up Later'],
  'Appointment Set': ['Attended', 'No-show'],
  Attended: ['Lost', 'Follow-up Later'],
  'No-show': ['Appointment Set', 'Follow-up Later', 'Lost'],
  'Follow-up Later': ['Contacted', 'Interested', 'Appointment Set', 'Lost'],
  Registered: [],
  // Graph-allowed, but role-gated below — reopening a Lost lead is Sales Manager/Admin only, not
  // even the original owning agent (sales.service.ts `transitionStatus`).
  Lost: ['Follow-up Later'],
};

/** The next-status options this user may actually submit from `current` — filters out
 * `Lost -> Follow-up Later` for anyone but a Sales Manager/Admin. */
export function allowedNextStatuses(current: LeadStatus, canReopenLost: boolean): LeadStatus[] {
  const next = LEAD_TRANSITIONS[current] ?? [];
  if (current === 'Lost' && !canReopenLost) {
    return next.filter((status) => status !== 'Follow-up Later');
  }
  return next;
}

export type LeadStatusSubField = 'appointmentDateTime' | 'lostReason' | 'nextFollowUpDate';

/** The one required sub-field a given target status needs on `PATCH /leads/{id}/status`
 * (`lead-status-transition.dto.ts` cross-field rules) — `null` when the target needs nothing extra
 * (e.g. `Contacted`, `Attended`/`No-show`, whose outcome is derived server-side from the status
 * itself, never sent by the client). */
export function subFieldFor(status: LeadStatus): LeadStatusSubField | null {
  if (status === 'Appointment Set') return 'appointmentDateTime';
  if (status === 'Lost') return 'lostReason';
  if (status === 'Follow-up Later') return 'nextFollowUpDate';
  return null;
}

const TONE_BY_STATUS: Record<LeadStatus, ChipTone> = {
  'New Lead': 'neutral',
  Contacted: 'info',
  Waiting: 'warning',
  Interested: 'info',
  'Appointment Set': 'info',
  Attended: 'success',
  'No-show': 'warning',
  Registered: 'success',
  Lost: 'danger',
  'Follow-up Later': 'warning',
};

export function toneForLeadStatus(status: LeadStatus): ChipTone {
  return TONE_BY_STATUS[status] ?? 'neutral';
}
